import {
    Mesh,
    OrthographicCamera,
    PlaneGeometry,
    Scene,
    ShaderMaterial,
    Texture,
    UnsignedByteType,
    WebGLRenderer,
    WebGLRenderTarget
} from "three";
import {
    ExposureCalculationMaterialFactory
} from "features/apartmentViewer/ApartmentViewer/shaders/exposureCalculationMaterial/ExposureCalculationMaterialFactory";

export class ExposureManager {

    scene: Scene;
    camera: OrthographicCamera;
    material: ShaderMaterial;
    plane: Mesh;
    renderTexture: WebGLRenderTarget;
    pixelBuffer: Uint8Array;
    defaultFovyScale = 0.6;

    static histogramWidth = 256;
    static histogramHeight = 144;

    private previousExposure: number = 0;

    constructor(
        private renderer: WebGLRenderer,
    ) {

        // Set up scene, camera, material, and plane
        this.scene = new Scene();
        this.camera = new OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
        this.material = ExposureCalculationMaterialFactory.createMaterial();

        // Create a plane mesh and add it to the scene
        const geometry = new PlaneGeometry(1, 1);
        this.plane = new Mesh(geometry, this.material);
        this.scene.add(this.plane);

        // Initialize render texture and pixel buffer for reading exposure data
        this.renderTexture = new WebGLRenderTarget(ExposureManager.histogramWidth, ExposureManager.histogramHeight, {
            type: UnsignedByteType,
            depthBuffer: false,
            stencilBuffer: false,
        });

        this.pixelBuffer = new Uint8Array(ExposureManager.histogramWidth * ExposureManager.histogramHeight * 4);
    }

    // Unpack the packed floating-point value from 4-byte RGBA
    unpack(r: number, g: number, b: number, a: number): number {
        return 65536 * r + 256 * g + b + a / 255;
    }

    getSmoothExposure(texture: Texture): number {
        // Calculate the raw exposure value based on the texture
        const currentExposure = this.calculateRawExposure(texture);

        // Apply exponential smoothing to the exposure value
        const alpha = 0.1; // Smoothing factor, adjust as needed for responsiveness vs smoothness
        const smoothedExposure = (1 - alpha) * this.previousExposure + alpha * currentExposure;

        // Store the smoothed value for the next frame
        this.previousExposure = smoothedExposure;

        return smoothedExposure;
    }

    private calculateRawExposure(texture: Texture): number {
        this.setupShader(texture);
        this.renderToTexture();
        this.readPixelsFromRenderTarget();
        return this.calculateAverageExposure();
    }

    private setupShader(texture: Texture) {
        this.material.uniforms.u_image.value = texture;
        this.material.uniforms.u_fovy_scale.value = this.defaultFovyScale; // Consider making `0.6` a configurable parameter.
    }

    private renderToTexture() {
        this.renderer.setRenderTarget(this.renderTexture);
        this.renderer.render(this.scene, this.camera);
        this.renderer.setRenderTarget(null);
    }

    private readPixelsFromRenderTarget() {
        this.renderer.readRenderTargetPixels(
            this.renderTexture,
            0, 0,
            ExposureManager.histogramWidth,
            ExposureManager.histogramHeight,
            this.pixelBuffer
        );
    }

    private calculateAverageExposure(): number {
        if (this.pixelBuffer.length === 0) {
            return 0;
        }

        let sumLogBrightness = 0;
        let pixelCount = 0;

        for (let i = 0; i < this.pixelBuffer.length; i += 4) {
            const brightness = this.unpack(
                this.pixelBuffer[i],
                this.pixelBuffer[i + 1],
                this.pixelBuffer[i + 2],
                this.pixelBuffer[i + 3]
            );

            if (brightness > 0) {
                sumLogBrightness += Math.log2(1 / brightness);
                pixelCount++;
            }
        }

        return pixelCount > 0 ? sumLogBrightness / pixelCount : 0;
    }

}
